import React, { createContext, useContext } from 'react';
import { assetUrl } from '../../lib/resources';

// A small markdown renderer that builds React elements only (never HTML strings), so text from the database can
// not inject markup or scripts. Supports headings, paragraphs, lists (one nested level), quotes, code, tables,
// rules, **bold**, *italic*, `code`, [links](https://...) and ![images](https://...).

const SAFE_URL = /^(https?:\/\/|\/(?!\/)|#|mailto:)/i;

// The admin preview swaps in its own image component (unpublished uploads need the admin token to load).
export const MarkdownImageContext = createContext(null);

const MdImage = ({ src, alt }) => {
  const Custom = useContext(MarkdownImageContext);
  if (Custom) return <Custom src={src} alt={alt} />;
  return <img src={assetUrl(src)} alt={alt} loading="lazy" decoding="async" className="my-6 h-auto max-w-full rounded-xl border border-gray-100" />;
};

export const headingId = (text) =>
  String(text).toLowerCase().replace(/[`*_[\]()]/g, '').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').slice(0, 80) || 'section';

const plain = (s) => s.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1').replace(/\[([^\]]*)\]\([^)]*\)/g, '$1').replace(/[`*_]/g, '');

// Headings for a table of contents (## and ###), with the same ids the renderer gives them.
export const extractHeadings = (md) => {
  const out = [];
  const seen = {};
  let fence = false;
  (md || '').split('\n').forEach((line) => {
    if (/^\s*```/.test(line)) fence = !fence;
    const m = !fence && line.match(/^(#{1,4})\s+(.+?)\s*#*\s*$/);
    if (m) {
      const level = Math.max(2, m[1].length);
      const text = plain(m[2]);
      let id = headingId(text);
      seen[id] = (seen[id] || 0) + 1;
      if (seen[id] > 1) id = `${id}-${seen[id]}`;
      if (level <= 3) out.push({ level, text, id });
    }
  });
  return out;
};

const INLINE = /(`[^`]+`)|(!\[[^\]]*\]\([^)\s]+\))|(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*\s][^*]*\*)|(_[^_\s][^_]*_)/;

function inline(text, keyBase = 'i') {
  const out = [];
  let rest = text;
  let k = 0;
  while (rest) {
    const m = rest.match(INLINE);
    if (!m) { out.push(rest); break; }
    if (m.index) out.push(rest.slice(0, m.index));
    const tok = m[0];
    const key = `${keyBase}-${k++}`;
    if (m[1]) {
      out.push(<code key={key} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.9em] text-[#0a1f2e]">{tok.slice(1, -1)}</code>);
    } else if (m[2]) {
      const [, alt, src] = tok.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
      out.push(SAFE_URL.test(src) && !src.startsWith('mailto:')
        ? <MdImage key={key} src={src} alt={alt} />
        : alt);
    } else if (m[3]) {
      const [, label, href] = tok.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (SAFE_URL.test(href)) {
        const external = /^https?:\/\//i.test(href);
        out.push(
          <a key={key} href={assetUrl(href)} className="font-medium text-[#0a1f2e] underline decoration-[#c8ff00] decoration-2 underline-offset-2 hover:decoration-[#0a1f2e]"
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
            {inline(label, key)}
          </a>,
        );
      } else {
        out.push(label);
      }
    } else if (m[4] || m[5]) {
      out.push(<strong key={key} className="font-semibold text-[#0a1f2e]">{inline(tok.slice(2, -2), key)}</strong>);
    } else {
      out.push(<em key={key}>{inline(tok.slice(1, -1), key)}</em>);
    }
    rest = rest.slice(m.index + tok.length);
  }
  return out;
}

const LIST_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const splitRow = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

function parseList(lines, start) {
  // returns [element data, next index]
  const base = lines[start].match(LIST_ITEM);
  const indent = base[1].length;
  const ordered = /\d/.test(base[2]);
  const items = [];
  let i = start;
  while (i < lines.length) {
    const m = lines[i].match(LIST_ITEM);
    if (m && m[1].length === indent && /\d/.test(m[2]) === ordered) {
      items.push({ text: m[3], children: null });
      i += 1;
    } else if (m && m[1].length > indent && items.length) {
      const [sub, next] = parseList(lines, i);
      items[items.length - 1].children = sub;
      i = next;
    } else if (!m && lines[i].trim() && /^\s{2,}/.test(lines[i]) && items.length) {
      items[items.length - 1].text += ` ${lines[i].trim()}`;
      i += 1;
    } else {
      break;
    }
  }
  return [{ ordered, items }, i];
}

function renderList(list, key) {
  const Tag = list.ordered ? 'ol' : 'ul';
  return (
    <Tag key={key} className={`my-5 space-y-2 pl-6 text-gray-700 ${list.ordered ? 'list-decimal' : 'list-disc'} marker:text-gray-400`}>
      {list.items.map((it, j) => (
        <li key={j} className="pl-1 leading-relaxed">
          {inline(it.text, `${key}-${j}`)}
          {it.children && renderList(it.children, `${key}-${j}-s`)}
        </li>
      ))}
    </Tag>
  );
}

const Markdown = ({ source, className = '' }) => {
  const lines = (source || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  const seen = {};
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const key = `b${i}`;
    if (!line.trim()) { i += 1; continue; }

    if (/^\s*```/.test(line)) {
      const code = [];
      i += 1;
      while (i < lines.length && !/^\s*```/.test(lines[i])) { code.push(lines[i]); i += 1; }
      i += 1;
      blocks.push(<pre key={key} className="my-6 overflow-x-auto rounded-xl bg-[#0a1f2e] p-4 font-mono text-sm leading-relaxed text-white/90"><code>{code.join('\n')}</code></pre>);
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.+?)\s*#*\s*$/);
    if (h) {
      const level = Math.max(2, h[1].length);
      const text = plain(h[2]);
      let id = headingId(text);
      seen[id] = (seen[id] || 0) + 1;
      if (seen[id] > 1) id = `${id}-${seen[id]}`;
      const cls = level === 2
        ? 'mt-12 mb-4 scroll-mt-28 font-display text-2xl font-semibold text-[#0a1f2e] first:mt-0'
        : 'mt-8 mb-3 scroll-mt-28 font-display text-lg font-semibold text-[#0a1f2e]';
      blocks.push(React.createElement(`h${level}`, { key, id, className: cls }, inline(h[2], key)));
      i += 1;
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      blocks.push(<hr key={key} className="my-10 border-gray-200" />);
      i += 1;
      continue;
    }
    if (/^\s*>/.test(line)) {
      const quote = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) { quote.push(lines[i].replace(/^\s*>\s?/, '')); i += 1; }
      blocks.push(
        <blockquote key={key} className="my-6 rounded-r-xl border-l-4 border-[#c8ff00] bg-[#f5f7f9] px-5 py-4 text-gray-700">
          {inline(quote.join(' '), key)}
        </blockquote>,
      );
      continue;
    }
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(lines[i + 1])) {
      const head = splitRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) { rows.push(splitRow(lines[i])); i += 1; }
      blocks.push(
        <div key={key} className="my-6 overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#f5f7f9] text-[#0a1f2e]">
              <tr>{head.map((c, j) => <th key={j} className="px-4 py-2.5 font-semibold">{inline(c, `${key}h${j}`)}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {rows.map((r, ri) => (
                <tr key={ri}>{head.map((_, j) => <td key={j} className="px-4 py-2.5 align-top">{inline(r[j] || '', `${key}r${ri}${j}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (LIST_ITEM.test(line)) {
      const [list, next] = parseList(lines, i);
      blocks.push(renderList(list, key));
      i = next;
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|\s*```|\s*>)/.test(lines[i]) && !(para.length && LIST_ITEM.test(lines[i]))) {
      para.push(lines[i].trim());
      i += 1;
    }
    blocks.push(<p key={key} className="my-4 leading-relaxed text-gray-700">{inline(para.join(' '), key)}</p>);
  }
  return <div className={`text-[17px] ${className}`}>{blocks}</div>;
};

export default Markdown;
